import React, { useState, useEffect, useCallback } from 'react';
import {
    Truck, Search, Plus, X, Car, Mail, Phone, Trash,
    ChevronLeft, ChevronRight, ChevronDown, MapPin, Loader2, AlertCircle,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    fetchFleetCustomers,
    createFleetCustomer,
    setFleetSearch,
    clearFleetErrors,
    selectFleetCustomers,
    selectFleetLoading,
    selectFleetError,
    selectFleetPage,
    selectFleetTotalPages,
    selectFleetTotalElements,
    selectFleetSearch,
    selectFleetCreating,
    selectFleetCreateError,
} from '../store/slices/fleetSlice';
import Layout from './Layout';
import CustomersTab from './CustomersTab';
import { deleteCustomerApi } from '../services/customerApi';
import CustomerReviewsTab from './CustomerReviewsTab';
import DeleteCustomerDialog from './DeleteCustomerDialog';
import { hasUiView, hasPermission } from '../services/authService';
import { UiViewKey } from '../constants/uiViewKeys';
import { rangeFromMonth, currentMonthValue, shiftMonth } from '../utils/monthRange';

const PAGE_SIZE = 20;

/*
 * Fleet and walk-in customers are the same table (Customer.fleet), so they belong on one
 * screen. They are separate TABS rather than one merged list because the two have almost
 * nothing in common operationally: fleet customers have portal logins and are invited;
 * walk-ins are created by a booking and are the ones whose details need correcting.
 */
const TABS = [
    { key: 'fleet',    label: 'Fleet customers' },
    { key: 'nonfleet', label: 'Customers' },
    /*
     * Reviews sits here rather than in the sidebar because it is about the same people
     * the other two tabs manage. Gated on its own uiView key, not on the page's: a
     * workshop may well want front-desk staff correcting customer records without
     * showing them what customers scored the business. The server enforces the same
     * split with feedback:read, so hiding the tab is presentation only.
     */
    { key: 'reviews',  label: 'Reviews', viewKey: UiViewKey.CUSTOMER_REVIEWS },
];

// ============================================
// Add Fleet Customer dialog
// ============================================

const AddFleetCustomerDialog = ({ open, onClose, onSubmit, submitting, error }) => {
    const [name, setName] = useState('');
    const [contactNumber, setContactNumber] = useState('');
    const [email, setEmail] = useState('');
    const [localError, setLocalError] = useState(null);

    // Reset fields whenever the dialog is (re)opened.
    useEffect(() => {
        if (open) {
            setName('');
            setContactNumber('');
            setEmail('');
            setLocalError(null);
        }
    }, [open]);

    if (!open) return null;

    const handleSubmit = () => {
        setLocalError(null);
        if (!name.trim()) {
            setLocalError('Enter the fleet customer name.');
            return;
        }
        if (!email.trim()) {
            setLocalError('Enter an email — the invite is sent here.');
            return;
        }
        onSubmit({
            name: name.trim(),
            contactNumber: contactNumber.trim() || null,
            email: email.trim(),
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/50" onClick={submitting ? undefined : onClose} />

            {/* Panel */}
            <div className="relative bg-white rounded-2xl border border-gray-200 shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-100">
                            <Truck size={18} className="text-emerald-600" />
                        </div>
                        <h2 className="text-lg font-display font-semibold text-gray-900">
                            Add fleet customer
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors bg-transparent border-none cursor-pointer disabled:opacity-50"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <p className="text-sm text-gray-500">
                        We'll create the fleet customer and email them a temporary password to
                        set up their portal. They can then manage their own vehicles and bookings.
                    </p>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Name
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Melbourne Couriers Pty Ltd"
                            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Contact number <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <input
                            type="tel"
                            value={contactNumber}
                            onChange={(e) => setContactNumber(e.target.value)}
                            placeholder="e.g. 03 9000 0000"
                            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Email
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="fleet@example.com"
                            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                    </div>

                    {(localError || error) && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-xl">
                            <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-600">{localError || error}</p>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors bg-transparent border-none cursor-pointer disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors text-sm font-semibold cursor-pointer disabled:opacity-60 border-none"
                    >
                        {submitting && <Loader2 size={16} className="animate-spin" />}
                        {submitting ? 'Sending invite…' : 'Create & send invite'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// Fleet Customers page
// ============================================

/**
 * Where a fleet customer's mail goes, as one line.
 *
 * Returns null rather than an empty string when nothing is set, so the caller can choose
 * what "no address" looks like instead of rendering a stray comma. The fleet customer
 * maintains this from their own portal — the workshop sees it, and cannot type it.
 */
export const formatAddress = (c) => {
    const parts = [c?.address, c?.suburb, c?.state, c?.postcode]
        .map((p) => (typeof p === 'string' ? p.trim() : p))
        .filter(Boolean);
    return parts.length ? parts.join(', ') : null;
};

/** The vehicles panel a fleet customer's row opens to show. */
const VehicleList = ({ vehicles }) => {
    if (!vehicles?.length) {
        return (
            <p className="text-sm text-gray-500">
                No vehicles yet — this customer adds their own from the fleet portal.
            </p>
        );
    }
    return (
        <ul className="divide-y divide-gray-100">
            {vehicles.map((v) => (
                <li key={v.uuid} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
                    <Car size={14} className="text-gray-400 flex-shrink-0" />
                    <span className="font-mono text-xs font-semibold text-gray-900">
                        {v.regoNumber || '—'}
                    </span>
                    <span className="text-sm text-gray-600 truncate">
                        {v.displayName || [v.year, v.make, v.model].filter(Boolean).join(' ') || 'Vehicle'}
                    </span>
                </li>
            ))}
        </ul>
    );
};

const FleetCustomersPage = () => {
    const dispatch = useAppDispatch();

    const customers = useAppSelector(selectFleetCustomers);
    const loading = useAppSelector(selectFleetLoading);
    const error = useAppSelector(selectFleetError);
    const page = useAppSelector(selectFleetPage);
    const totalPages = useAppSelector(selectFleetTotalPages);
    const totalElements = useAppSelector(selectFleetTotalElements);
    const search = useAppSelector(selectFleetSearch);
    const creating = useAppSelector(selectFleetCreating);
    const createError = useAppSelector(selectFleetCreateError);

    const [activeTab, setActiveTab] = useState('fleet');
    /*
     * Read once per mount: the claim only changes with a new token, and that path
     * remounts the page. hasUiView decodes the JWT on every call.
     */
    const [visibleTabs] = useState(
        () => TABS.filter((t) => !t.viewKey || hasUiView(t.viewKey)));
    /*
     * Fleet browse is bounded to a month, exactly like the walk-in tab. A search reaches
     * fleet customers of any date; without one the server requires the window.
     */
    const [fleetMonth, setFleetMonth] = useState(currentMonthValue());
    const [dialogOpen, setDialogOpen] = useState(false);
    const [canDeleteCustomer] = useState(() => hasPermission('customer:delete'));
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [searchInput, setSearchInput] = useState(search || '');

    const load = useCallback((opts = {}) => {
        const term = opts.search ?? searchInput;
        const range = rangeFromMonth(opts.monthValue ?? fleetMonth);
        dispatch(fetchFleetCustomers({
            page: opts.page ?? 0,
            size: PAGE_SIZE,
            search: term,
            createdFrom: range.from,
            createdTo: range.to,
        }));
    }, [dispatch, searchInput, fleetMonth]);

    // Opens on this month's new fleet customers rather than the whole list.
    useEffect(() => {
        const range = rangeFromMonth(currentMonthValue());
        dispatch(fetchFleetCustomers({
            page: 0, size: PAGE_SIZE, search: '',
            createdFrom: range.from, createdTo: range.to,
        }));
    }, [dispatch]);

    // Debounced search — re-query 400ms after typing stops. A search ignores the month.
    useEffect(() => {
        const t = setTimeout(() => {
            const range = rangeFromMonth(fleetMonth);
            dispatch(setFleetSearch(searchInput));
            dispatch(fetchFleetCustomers({
                page: 0, size: PAGE_SIZE, search: searchInput,
                createdFrom: range.from, createdTo: range.to,
            }));
        }, 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

    const goFleetMonth = (next) => {
        setFleetMonth(next);
        load({ page: 0, search: '', monthValue: next });
    };

    const handleCreate = async (payload) => {
        const result = await dispatch(createFleetCustomer(payload));
        if (createFleetCustomer.fulfilled.match(result)) {
            setDialogOpen(false);
            dispatch(clearFleetErrors());
            load({ page: 0 });
        }
    };

    const askDelete = (c) => {
        setDeleteError(null);
        setPendingDelete(c);
    };

    const confirmDelete = async () => {
        setDeleting(true);
        setDeleteError(null);
        try {
            await deleteCustomerApi(pendingDelete.uuid);
            setPendingDelete(null);
            load({ page: 0 });
        } catch (e) {
            // A fleet customer almost always has a portal login, and that blocks the
            // delete. The server says so explicitly; keep the dialog open so the message
            // is read rather than flashing past.
            setDeleteError(e?.response?.data?.message || 'Could not delete this customer.');
        } finally {
            setDeleting(false);
        }
    };

    /*
     * Which fleet customer's row is open. One at a time: the expanded panel is tall
     * (a vehicle each), and several open at once turns the table into a scroll with no
     * shape. The row itself carries its vehicles, so opening one fetches nothing.
     */
    const [expandedUuid, setExpandedUuid] = useState(null);

    const toggleRow = (uuid) => setExpandedUuid((current) => (current === uuid ? null : uuid));

    const goToPage = (target) => {
        if (target < 0 || target >= totalPages) return;
        dispatch(fetchFleetCustomers({ page: target, size: PAGE_SIZE, search: searchInput }));
    };

    return (
        <Layout>
            <div className="px-6 py-8">
                {/* Page header */}
                <div className="flex items-start justify-between mb-6 gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-100">
                            <Truck size={22} className="text-emerald-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-display font-bold text-gray-900">
                                Customers
                            </h1>
                            <p className="text-sm text-gray-500 mt-0.5">
                                {activeTab === 'fleet'
                                    ? 'Invite fleet customers and manage their portal access.'
                                    : activeTab === 'reviews'
                                        ? 'How customers rated you after collecting their vehicle.'
                                        : 'Find a customer to correct their details or their vehicles.'}
                            </p>
                        </div>
                    </div>

                </div>

                {/* Tabs */}
                <div className="mb-4 flex gap-1 border-b border-gray-200">
                    {visibleTabs.map(({ key, label }) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => setActiveTab(key)}
                            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                                activeTab === key
                                    ? 'border-emerald-600 text-emerald-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-800'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {activeTab === 'nonfleet' && <CustomersTab />}
                {activeTab === 'reviews' && <CustomerReviewsTab />}

                {/* Table card — fleet tab only */}
                {activeTab === 'fleet' && (
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    {/* Search + month browse + the invite action, all inside the tab so the
                        header is not carrying a control that belongs to one of two views. */}
                    <div className="p-4 border-b border-gray-100 space-y-3">
                        <div className="flex items-center gap-2">
                        <div className="relative flex-1 max-w-md">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Search by name, contact number or rego"
                                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            />
                        </div>

                        <button
                            onClick={() => { dispatch(clearFleetErrors()); setDialogOpen(true); }}
                            className="inline-flex flex-shrink-0 items-center gap-2 rounded-xl border-none bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 cursor-pointer"
                        >
                            <Plus size={18} />
                            <span className="hidden sm:inline">Add fleet customer</span>
                            <span className="sm:hidden">Add</span>
                        </button>
                        </div>

                        {/* Month navigation — hidden while searching, because a search
                            deliberately ignores the window and showing it would imply a
                            filter that is not being applied. */}
                        {!searchInput.trim() ? (
                            <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                                <button type="button" onClick={() => goFleetMonth(shiftMonth(fleetMonth, -1))} disabled={loading}
                                        className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-gray-600 hover:bg-white disabled:opacity-40">
                                    <ChevronLeft size={14} /> Previous
                                </button>
                                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
                                    <span>New fleet customers</span>
                                    {/* Editable: fleet customers are onboarded rarely, so
                                        the useful month is often far back — twelve clicks
                                        on Previous is not a way to find them. */}
                                    <input
                                        type="month"
                                        value={fleetMonth}
                                        max={currentMonthValue()}
                                        disabled={loading}
                                        onChange={(e) => e.target.value && goFleetMonth(e.target.value)}
                                        className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm font-medium text-gray-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
                                    />
                                </label>
                                <button type="button" onClick={() => goFleetMonth(shiftMonth(fleetMonth, 1))}
                                        disabled={loading || fleetMonth >= currentMonthValue()}
                                        className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-gray-600 hover:bg-white disabled:opacity-40">
                                    Next <ChevronRight size={14} />
                                </button>
                            </div>
                        ) : (
                            <p className="text-xs text-gray-500">
                                Showing search results across all dates.
                            </p>
                        )}
                    </div>

                    {/* Table body states */}
                    {loading ? (
                        <div className="py-16 flex flex-col items-center justify-center">
                            <Loader2 size={28} className="text-emerald-500 animate-spin" />
                            <p className="mt-3 text-sm text-gray-500">Loading fleet customers…</p>
                        </div>
                    ) : error ? (
                        <div className="py-16 flex flex-col items-center justify-center px-6 text-center">
                            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-3">
                                <AlertCircle size={22} className="text-red-500" />
                            </div>
                            <p className="text-sm text-gray-900 font-medium">Couldn't load fleet customers</p>
                            <p className="text-sm text-gray-500 mt-1">{error}</p>
                            <button
                                onClick={() => load({ page: 0 })}
                                className="mt-4 px-4 py-2 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer border-none"
                            >
                                Try again
                            </button>
                        </div>
                    ) : customers.length === 0 ? (
                        <div className="py-16 flex flex-col items-center justify-center px-6 text-center">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                <Truck size={22} className="text-gray-400" />
                            </div>
                            {/* "None yet" would be a lie while browsing: the list is
                                bounded to a month, and fleet customers are onboarded
                                rarely, so an empty month is the normal case. */}
                            <p className="text-sm text-gray-900 font-medium">
                                {searchInput
                                    ? 'No fleet customers match your search'
                                    : `No new fleet customers in ${rangeFromMonth(fleetMonth).label}`}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                                {searchInput
                                    ? 'Try a different name, contact number or rego.'
                                    : 'Search above to find an existing one, or step back a month.'}
                            </p>
                            {!searchInput && (
                                <button
                                    onClick={() => { dispatch(clearFleetErrors()); setDialogOpen(true); }}
                                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-colors cursor-pointer border-none"
                                >
                                    <Plus size={16} />
                                    Add fleet customer
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* Desktop table */}
                            <div className="hidden sm:block overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                    <tr className="border-b border-gray-100">
                                        <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Name</th>
                                        <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Contact number</th>
                                        <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Address</th>
                                        <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Vehicles</th>
                                        {canDeleteCustomer && <th className="w-px px-6 py-3"><span className="sr-only">Actions</span></th>}
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                    {customers.map((c) => {
                                        const open = expandedUuid === c.uuid;
                                        const address = formatAddress(c);
                                        return (
                                        <React.Fragment key={c.uuid}>
                                        <tr className="hover:bg-gray-50 transition-colors cursor-pointer"
                                            onClick={() => toggleRow(c.uuid)}>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    {/* The chevron is the affordance; the whole row is the
                                                        target, because a 14px icon is not one on a tablet
                                                        at a service desk. */}
                                                    <button type="button"
                                                            onClick={(e) => { e.stopPropagation(); toggleRow(c.uuid); }}
                                                            aria-expanded={open}
                                                            aria-label={`${open ? 'Hide' : 'Show'} vehicles for ${c.name}`}
                                                            className="border-none bg-transparent p-0 text-gray-400 cursor-pointer">
                                                        <ChevronDown size={16}
                                                                     className={`transition-transform ${open ? '' : '-rotate-90'}`} />
                                                    </button>
                                                    <p className="text-sm font-medium text-gray-900">{c.name}</p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                    <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                                                        {c.contactNumber ? (
                                                            <>
                                                                <Phone size={14} className="text-gray-400" />
                                                                {c.contactNumber}
                                                            </>
                                                        ) : (
                                                            <span className="text-gray-400">—</span>
                                                        )}
                                                    </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                    <span className="inline-flex items-start gap-1.5 text-sm text-gray-600">
                                                        {address ? (
                                                            <>
                                                                <MapPin size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                                                <span className="max-w-xs">{address}</span>
                                                            </>
                                                        ) : (
                                                            <span className="text-gray-400">—</span>
                                                        )}
                                                    </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-lg text-sm font-medium text-gray-700">
                                                        <Car size={14} className="text-gray-400" />
                                                        {c.vehicleCount ?? 0}
                                                    </span>
                                            </td>
                                            {canDeleteCustomer && (
                                                <td className="px-6 py-4 text-right">
                                                    {/* Presentation only — the server enforces
                                                        customer:delete regardless. */}
                                                    <button type="button"
                                                            onClick={(e) => { e.stopPropagation(); askDelete(c); }}
                                                            aria-label={`Delete ${c.name}`}
                                                            className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600">
                                                        <Trash size={12} className="mr-1 inline" />Delete
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                        {open && (
                                            <tr className="bg-gray-50/60">
                                                <td colSpan={canDeleteCustomer ? 5 : 4} className="px-6 py-4">
                                                    <div className="pl-6">
                                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                                            Vehicles
                                                        </p>
                                                        <VehicleList vehicles={c.vehicles} />
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        </React.Fragment>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile cards */}
                            <div className="sm:hidden divide-y divide-gray-50">
                                {customers.map((c) => {
                                    const open = expandedUuid === c.uuid;
                                    const address = formatAddress(c);
                                    return (
                                    <div key={c.uuid} className="px-4 py-4">
                                        <div className="flex items-start justify-between gap-3"
                                             onClick={() => toggleRow(c.uuid)}>
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                                                    <ChevronDown size={14}
                                                                 className={`text-gray-400 flex-shrink-0 transition-transform ${open ? '' : '-rotate-90'}`} />
                                                    {c.name}
                                                </p>
                                                <p className="text-sm text-gray-500 mt-1 flex items-center gap-1.5">
                                                    <Phone size={13} className="text-gray-400" />
                                                    {c.contactNumber || '—'}
                                                </p>
                                                {address && (
                                                    <p className="text-sm text-gray-500 mt-1 flex items-start gap-1.5">
                                                        <MapPin size={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                                        <span>{address}</span>
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex flex-shrink-0 items-center gap-2">
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-lg text-sm font-medium text-gray-700">
                                                    <Car size={14} className="text-gray-400" />
                                                    {c.vehicleCount ?? 0}
                                                </span>
                                                {canDeleteCustomer && (
                                                    <button type="button"
                                                            onClick={(e) => { e.stopPropagation(); askDelete(c); }}
                                                            aria-label={`Delete ${c.name}`}
                                                            className="rounded-lg border border-gray-200 p-1.5 text-gray-500">
                                                        <Trash size={13} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        {open && (
                                            <div className="mt-3 pl-5">
                                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                                    Vehicles
                                                </p>
                                                <VehicleList vehicles={c.vehicles} />
                                            </div>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>

                            {/* Pagination footer */}
                            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                                <p className="text-sm text-gray-500">
                                    {totalElements} {totalElements === 1 ? 'customer' : 'customers'}
                                </p>
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => goToPage(page - 1)}
                                            disabled={page <= 0}
                                            className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors bg-transparent cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <ChevronLeft size={16} />
                                        </button>
                                        <span className="text-sm text-gray-600 px-2">
                                            Page {page + 1} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => goToPage(page + 1)}
                                            disabled={page >= totalPages - 1}
                                            className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors bg-transparent cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <ChevronRight size={16} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
                )}
            </div>

            <DeleteCustomerDialog
                open={!!pendingDelete}
                customer={pendingDelete}
                onConfirm={confirmDelete}
                onClose={() => setPendingDelete(null)}
                deleting={deleting}
                error={deleteError}
            />

            <AddFleetCustomerDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSubmit={handleCreate}
                submitting={creating}
                error={createError}
            />
        </Layout>
    );
};

export default FleetCustomersPage;